import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Edit2, X, Loader2, Package, DollarSign, Save, Zap, Boxes, AlertTriangle } from 'lucide-react'
import { getSkuRates, saveSkuRates, getSkuCartons, saveSkuCartons, getOrderItemsSummary } from '../lib/api'
import { useToast, ToastContainer } from './Toast'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}

const currentMonthStart = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

// Parse insert_trigger_sku field (single SKU, comma-separated, or array) to array.
function parseTriggerSkus(raw) {
  if (!raw) return []
  const list = Array.isArray(raw) ? raw : String(raw).split(/[,;\n\r\t]+/)
  const seen = new Set()
  const out = []
  for (const s of list) {
    const t = (s ?? '').trim()
    if (t && !seen.has(t)) { seen.add(t); out.push(t) }
  }
  return out
}

// Format a SKU rate's effective window for the pill: "from 1 Jun 2026",
// "until 31 Aug 2026", "1 Jun 2026 – 31 Aug 2026", or null when undated
// ("always"). Parse as local midnight so the date can't slip a day by timezone.
function fmtSkuWindow(r) {
  const d = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  const f = r.start_date ? d(r.start_date) : null
  const t = r.end_date ? d(r.end_date) : null
  if (!f && !t) return null
  if (f && !t) return `from ${f}`
  if (!f && t) return `until ${t}`
  return `${f} – ${t}`
}

// ── SKU Pricing Modal ────────────────────────────────────────────────────────

function SkuPricingModal({ clientId, onClose }) {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const { data: rates = [], isLoading, error } = useQuery({
    queryKey: ['sku-rates', clientId],
    queryFn: () => getSkuRates(clientId),
  })

  const [rows, setRows] = useState(null)   // null = not yet initialised from server
  // Initialise rows once data arrives
  if (rates && rows === null && !isLoading) {
    setRows(rates.map(r => ({ ...r, _key: crypto.randomUUID() })))
  }
  const workingRows = rows ?? []

  const mutation = useMutation({
    mutationFn: (r) => saveSkuRates(clientId, r),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sku-rates', clientId] })
      queryClient.invalidateQueries({ queryKey: ['order-items', clientId] })
      addToast({ type: 'success', message: 'SKU rates saved.' })
      onClose()
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Save failed.' }),
  })

  function addRow() {
    setRows(prev => [...(prev ?? []), { _key: crypto.randomUUID(), sku: '', description: '', cost: '', start_date: '', end_date: '' }])
  }

  function removeRow(key) {
    setRows(prev => prev.filter(r => r._key !== key))
  }

  function updateRow(key, field, value) {
    setRows(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r))
  }

  // SKUs whose date windows overlap — warn (don't block); billing resolves an
  // overlap by taking the rate with the latest start date.
  const overlapSkus = (() => {
    const groups = {}
    workingRows.forEach(r => { const s = r.sku?.trim(); if (s) (groups[s] ??= []).push(r) })
    const overlaps = (rs) => {
      const rng = rs.map(r => ({
        s: r.start_date ? new Date(r.start_date).getTime() : -Infinity,
        e: r.end_date ? new Date(r.end_date).getTime() : Infinity,
      }))
      for (let i = 0; i < rng.length; i++)
        for (let j = i + 1; j < rng.length; j++)
          if (rng[i].s <= rng[j].e && rng[j].s <= rng[i].e) return true
      return false
    }
    return Object.entries(groups).filter(([, rs]) => rs.length > 1 && overlaps(rs)).map(([s]) => s)
  })()

  function handleSave() {
    const valid = workingRows
      .filter(r => r.sku?.trim())
      .map(r => ({
        sku: r.sku.trim(),
        description: r.description || null,
        cost: Number(r.cost) || 0,
        start_date: r.start_date || null,
        end_date: r.end_date || null,
      }))
    mutation.mutate(valid)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-800">SKU Pricing</h2>
            <p className="text-xs text-slate-500 mt-0.5">Per-unit costs for specific SKUs (charge = quantity × cost). Add a dated row to change a SKU's price over time — leave dates blank for "always".</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-400" /></div>
          ) : error ? (
            // On failure `data` is undefined and the `= []` default takes over,
            // so the editor would open empty — reading as "this client has no
            // SKU rates" instead of "we couldn't load them".
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-3 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Couldn&rsquo;t load SKU rates</p>
                <p className="text-xs text-red-600 mt-0.5">
                  This is <b>not</b> an empty rate list — don&rsquo;t save over it.
                </p>
                <p className="text-xs font-mono mt-1 break-all">
                  {error?.response?.data?.detail || error?.message || 'Unknown error'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Column headers */}
              {workingRows.length > 0 && (
                <div className="grid grid-cols-[1fr_1.4fr_0.8fr_1.1fr_1.1fr_auto] gap-2 px-1 mb-1">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">SKU</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost / Unit ($)</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Effective from</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Effective to</span>
                  <span />
                </div>
              )}

              {workingRows.map(row => (
                <div key={row._key} className="grid grid-cols-[1fr_1.4fr_0.8fr_1.1fr_1.1fr_auto] gap-2 items-center">
                  <input
                    type="text"
                    value={row.sku}
                    onChange={e => updateRow(row._key, 'sku', e.target.value)}
                    placeholder="e.g. SKU-001"
                    className="input text-sm font-mono"
                  />
                  <input
                    type="text"
                    value={row.description || ''}
                    onChange={e => updateRow(row._key, 'description', e.target.value)}
                    placeholder="e.g. Bubble wrap packing"
                    className="input text-sm"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={row.cost}
                    onChange={e => updateRow(row._key, 'cost', e.target.value)}
                    placeholder="0.00"
                    className="input text-sm"
                  />
                  <input
                    type="date"
                    value={row.start_date || ''}
                    onChange={e => updateRow(row._key, 'start_date', e.target.value)}
                    className="input text-sm"
                    title="Blank = from the beginning"
                  />
                  <input
                    type="date"
                    value={row.end_date || ''}
                    onChange={e => updateRow(row._key, 'end_date', e.target.value)}
                    className="input text-sm"
                    title="Blank = open-ended / current"
                  />
                  <button onClick={() => removeRow(row._key)} className="p-1.5 text-slate-400 hover:text-red-500 rounded">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              {overlapSkus.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                  <span className="flex-1">
                    Overlapping date ranges for <span className="font-mono font-semibold">{overlapSkus.join(', ')}</span> — where they overlap, billing uses the rate with the latest start date.
                  </span>
                </div>
              )}

              <button onClick={addRow} className="btn-secondary text-xs py-1.5 px-3 mt-2">
                <Plus size={13} /> Add SKU
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={mutation.isPending} className="btn-primary">
            {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Rates
          </button>
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}

// ── Carton Sizes Modal ───────────────────────────────────────────────────────

function CartonSizesModal({ clientId, onClose }) {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const { data: cartons = [], isLoading, error } = useQuery({
    queryKey: ['sku-cartons', clientId],
    queryFn: () => getSkuCartons(clientId),
  })

  const [rows, setRows] = useState(null)
  if (cartons && rows === null && !isLoading) {
    setRows(cartons.map(c => ({ ...c, _key: crypto.randomUUID() })))
  }
  const workingRows = rows ?? []

  const mutation = useMutation({
    mutationFn: (c) => saveSkuCartons(clientId, c),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sku-cartons', clientId] })
      addToast({ type: 'success', message: 'Carton sizes saved.' })
      onClose()
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Save failed.' }),
  })

  function addRow() {
    setRows(prev => [...(prev ?? []), { _key: crypto.randomUUID(), sku: '', carton_size: '', inners_per_outer: '' }])
  }
  function removeRow(key) {
    setRows(prev => prev.filter(r => r._key !== key))
  }
  function updateRow(key, field, value) {
    setRows(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r))
  }

  function handleSave() {
    const valid = workingRows
      .filter(r => r.sku?.trim() && Number(r.carton_size) > 0)
      .map(r => {
        // Only send an outer multiple when it's a real outer (>= 2 inners).
        const per = Math.floor(Number(r.inners_per_outer))
        return {
          sku: r.sku.trim(),
          carton_size: Math.floor(Number(r.carton_size)),
          inners_per_outer: per > 1 ? per : null,
        }
      })
    mutation.mutate(valid)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-800">Carton Sizes</h2>
            <p className="text-xs text-slate-500 mt-0.5">Units per inner carton per SKU. Optionally set how many inner cartons make one outer carton — a full outer bills as a single case pick. Full cartons bill at the case-pick rate; leftover units bill as additional items.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-400" /></div>
          ) : error ? (
            // Same trap as SKU Pricing: the `= []` default would render a blank
            // editor, which reads as "no carton sizes configured" — a billing
            // statement — rather than a failed load.
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-3 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Couldn&rsquo;t load carton sizes</p>
                <p className="text-xs text-red-600 mt-0.5">
                  This is <b>not</b> an empty carton list — don&rsquo;t save over it.
                </p>
                <p className="text-xs font-mono mt-1 break-all">
                  {error?.response?.data?.detail || error?.message || 'Unknown error'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {workingRows.length > 0 && (
                <div className="grid grid-cols-[2fr_1fr_1fr_auto] gap-2 px-1 mb-1">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">SKU</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Units / Inner</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Inners / Outer</span>
                  <span />
                </div>
              )}

              {workingRows.map(row => (
                <div key={row._key} className="grid grid-cols-[2fr_1fr_1fr_auto] gap-2 items-center">
                  <input
                    type="text"
                    value={row.sku}
                    onChange={e => updateRow(row._key, 'sku', e.target.value)}
                    placeholder="e.g. SKU-001"
                    className="input text-sm font-mono"
                  />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={row.carton_size ?? ''}
                    onChange={e => updateRow(row._key, 'carton_size', e.target.value)}
                    placeholder="e.g. 6"
                    className="input text-sm"
                  />
                  <input
                    type="number"
                    min="2"
                    step="1"
                    value={row.inners_per_outer ?? ''}
                    onChange={e => updateRow(row._key, 'inners_per_outer', e.target.value)}
                    placeholder="none"
                    title="How many inner cartons make one outer carton. Leave blank if there's no outer carton."
                    className="input text-sm"
                  />
                  <button onClick={() => removeRow(row._key)} className="p-1.5 text-slate-400 hover:text-red-500 rounded">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              <button onClick={addRow} className="btn-secondary text-xs py-1.5 px-3 mt-2">
                <Plus size={13} /> Add SKU
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={mutation.isPending} className="btn-primary">
            {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Carton Sizes
          </button>
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}

// ── Main Tab ─────────────────────────────────────────────────────────────────

export default function OrderDetailsTab({ clientId, insertTriggerSku }) {
  const triggerSkuList = parseTriggerSkus(insertTriggerSku)
  const triggerSkuSet = new Set(triggerSkuList)
  const [fromDate, setFromDate] = useState(currentMonthStart())
  const [toDate, setToDate]     = useState('')
  const [showPricing, setShowPricing] = useState(false)
  const [showCartons, setShowCartons] = useState(false)
  const { toasts, addToast, removeToast } = useToast()

  const params = {
    ...(fromDate && { from: fromDate }),
    ...(toDate   && { to:   toDate   }),
  }

  const { data: items = [], isLoading, error: itemsError } = useQuery({
    queryKey: ['order-items', clientId, params],
    queryFn: () => getOrderItemsSummary(clientId, params),
    enabled: !!clientId,
  })

  const { data: skuRates = [], error: skuRatesError } = useQuery({
    queryKey: ['sku-rates', clientId],
    queryFn: () => getSkuRates(clientId),
    enabled: !!clientId,
  })

  const { data: skuCartons = [], error: skuCartonsError } = useQuery({
    queryKey: ['sku-cartons', clientId],
    queryFn: () => getSkuCartons(clientId),
    enabled: !!clientId,
  })

  const totalCharge = items.reduce((sum, r) => sum + (r.total_charge || 0), 0)
  const pricedCount = new Set(items.filter(r => r.rate > 0).map(r => r.sku)).size
  const uniqueSkuCount = new Set(skuRates.map(r => r.sku)).size

  // SKU → carton size, so each line item can show whether carton pricing applies.
  const cartonBySku = {}
  skuCartons.forEach(c => { if (c.sku) cartonBySku[c.sku] = c.carton_size })

  return (
    <div className="space-y-4">
      {/* Carton Sizes card */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Carton Sizes</h3>
            {/* A failed fetch defaults to [] and would print the "no carton
                sizes set" line below — a claim about how this client bills. */}
            {skuCartonsError ? (
              <p className="text-xs text-red-700 mt-0.5 flex items-start gap-1">
                <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
                <span>
                  Couldn&rsquo;t load carton sizes — this is <b>not</b> &ldquo;none set&rdquo;.{' '}
                  <span className="font-mono break-all">
                    {skuCartonsError?.response?.data?.detail || skuCartonsError?.message || 'Unknown error'}
                  </span>
                </span>
              </p>
            ) : (
              <p className="text-xs text-slate-400 mt-0.5">
                {skuCartons.length === 0
                  ? 'No carton sizes set — orders bill as individual picks'
                  : `${skuCartons.length} SKU${skuCartons.length !== 1 ? 's' : ''} with carton pricing`}
              </p>
            )}
          </div>
          <button onClick={() => setShowCartons(true)} className="btn-secondary text-xs py-1 px-3">
            <Edit2 size={12} /> Edit Carton Sizes
          </button>
        </div>

        {skuCartons.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {skuCartons.map(c => (
              <span key={c.sku} className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-violet-50 text-violet-700">
                <Boxes size={10} className="text-violet-500" />
                <span className="font-mono">{c.sku}</span>
                <span className="font-semibold">
                  {c.inners_per_outer > 1
                    ? `${c.carton_size}/inner · ${c.carton_size * c.inners_per_outer}/outer`
                    : `${c.carton_size}/carton`}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* SKU Rates card */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">SKU Pricing</h3>
            {/* Without this branch the `= []` default prints "No SKU rates
                configured — all items shown at $0" on a fetch failure. */}
            {skuRatesError ? (
              <p className="text-xs text-red-700 mt-0.5 flex items-start gap-1">
                <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
                <span>
                  Couldn&rsquo;t load SKU rates — this is <b>not</b> &ldquo;none configured&rdquo;.{' '}
                  <span className="font-mono break-all">
                    {skuRatesError?.response?.data?.detail || skuRatesError?.message || 'Unknown error'}
                  </span>
                </span>
              </p>
            ) : (
              <p className="text-xs text-slate-400 mt-0.5">
                {skuRates.length === 0
                  ? 'No SKU rates configured — all items shown at $0'
                  : `${uniqueSkuCount} SKU${uniqueSkuCount !== 1 ? 's' : ''} priced`}
              </p>
            )}
          </div>
          <button onClick={() => setShowPricing(true)} className="btn-secondary text-xs py-1 px-3">
            <Edit2 size={12} /> Edit SKU Pricing
          </button>
        </div>

        {triggerSkuList.length > 0 && (
          <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <Zap size={12} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              Insert fee is charged once per order whenever any of these SKUs is present:
              <div className="mt-1 flex flex-wrap gap-1">
                {triggerSkuList.map(sku => (
                  <span key={sku} className="font-mono font-semibold bg-white/60 border border-amber-200 rounded px-1.5 py-0.5">
                    {sku}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {(skuRates.length > 0 || triggerSkuList.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {skuRates.map((r, i) => {
              const isTrigger = triggerSkuSet.has(r.sku)
              const win = fmtSkuWindow(r)
              return (
                <span key={r.id || `${r.sku}-${r.start_date || ''}-${i}`} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                  isTrigger
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'bg-blue-50 text-blue-700'
                }`}>
                  <span className="font-mono">{r.sku}</span>
                  {isTrigger && (
                    <Zap size={10} className="text-amber-500" title="Insert trigger SKU" />
                  )}
                  {r.description && (
                    <span className={isTrigger ? 'text-amber-500' : 'text-blue-500'}>
                      — {r.description}
                    </span>
                  )}
                  <span className="font-semibold">${Number(r.cost).toFixed(2)}/unit</span>
                  {win && <span className="opacity-70 font-normal">· {win}</span>}
                </span>
              )
            })}
            {/* Show any trigger SKUs that don't have a per-unit cost configured */}
            {triggerSkuList
              .filter(sku => !skuRates.some(r => r.sku === sku))
              .map(sku => (
                <span key={sku} className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium px-2.5 py-1 rounded-full">
                  <Zap size={10} className="text-amber-500" />
                  <span className="font-mono">{sku}</span>
                  <span className="text-amber-500">— insert trigger only</span>
                </span>
              ))}
          </div>
        )}
      </div>

      {/* Items table */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Order Line Items</h3>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <span className="text-slate-400 text-xs">to</span>
            <input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">SKU</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Qty</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Rate / Unit</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Charge</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {[1,2,3,4,5].map(j => (
                      <td key={j} className="px-4 py-3"><div className="skeleton h-4 rounded" /></td>
                    ))}
                  </tr>
                ))
              ) : itemsError ? (
                // The `= []` default would render the "no order items" state
                // below and drop the totals row, so a failed fetch reads as
                // "nothing to bill for this period".
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <AlertTriangle size={28} className="mx-auto text-red-400 mb-2" />
                    <p className="text-slate-600 text-sm font-medium">Couldn&rsquo;t load order line items</p>
                    <p className="text-slate-400 text-xs mt-1">
                      This is <b>not</b> an empty period — no total is being shown.
                    </p>
                    <p className="text-red-600 text-xs mt-2 font-mono break-all px-6">
                      {itemsError?.response?.data?.detail || itemsError?.message || 'Unknown error'}
                    </p>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <Package size={28} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-400 text-sm">No order items in this period</p>
                    <p className="text-slate-300 text-xs mt-1">Run a Full Sync on the Orders tab to pull line items from ShipStation</p>
                  </td>
                </tr>
              ) : (
                <>
                  {items.map((row, i) => (
                    <tr key={`${row.sku}-${row.rate}-${i}`} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {row.sku ? (
                            <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                              {row.sku}
                            </span>
                          ) : (
                            <span className="text-xs text-amber-600 italic">no SKU</span>
                          )}
                          {cartonBySku[row.sku] > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700">
                              <Boxes size={10} className="text-violet-500" />{cartonBySku[row.sku]}/carton
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">{row.product_name || '—'}</td>
                      <td className="px-3 py-3 text-right font-medium text-slate-700">{row.total_quantity.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right">
                        {row.rate > 0 ? (
                          <span className="text-blue-700 font-medium">{formatCurrency(row.rate)}</span>
                        ) : (
                          <span className="text-slate-300 text-xs">not priced</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {row.total_charge > 0 ? (
                          <span className="font-semibold text-slate-800">{formatCurrency(row.total_charge)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}

                  {/* Totals row */}
                  {pricedCount > 0 && (
                    <tr className="bg-slate-50 border-t-2 border-slate-200">
                      <td colSpan={4} className="px-4 py-3 text-right text-sm font-semibold text-slate-600">
                        Total SKU charges ({pricedCount} priced SKU{pricedCount !== 1 ? 's' : ''})
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-800 text-sm">
                        {formatCurrency(totalCharge)}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPricing && (
        <SkuPricingModal clientId={clientId} onClose={() => setShowPricing(false)} />
      )}

      {showCartons && (
        <CartonSizesModal clientId={clientId} onClose={() => setShowCartons(false)} />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}
